import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'Research', path: '/research' },
  { label: 'Teaching', path: '/teaching' },
  { label: 'Databooks', path: '/databook' },
  { label: 'About', path: '/about' },
];

export default function Navbar() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Lock body scroll when mobile menu is open (#45)
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close on Escape (#17) and trap focus (#18)
  const handleKeyDown = useCallback((e) => {
    if (!menuOpen) return;

    if (e.key === 'Escape') {
      setMenuOpen(false);
      toggleRef.current?.focus();
      return;
    }

    if (e.key === 'Tab' && menuRef.current) {
      const focusable = menuRef.current.querySelectorAll('a, button');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [menuOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Focus first menu link when opening
  useEffect(() => {
    if (menuOpen && menuRef.current) {
      const firstLink = menuRef.current.querySelector('a, button');
      firstLink?.focus();
    }
  }, [menuOpen]);

  return (
    <>
      <nav className="navbar" role="navigation" aria-label="Main navigation">
        <div className="navbar__inner container">
          <Link to="/" className="navbar__logo" aria-label="Home">
          </Link>
          <div className="navbar__right">
            <ul className="navbar__links navbar__links--desktop">
              {navItems.map(item => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`navbar__link ${location.pathname === item.path ? 'navbar__link--active' : ''}`}
                    {...(location.pathname === item.path ? { 'aria-current': 'page' } : {})}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://lumecon.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="navbar__link navbar__link--external"
                >
                  Lumecon
                </a>
              </li>
            </ul>
            <button
              ref={toggleRef}
              className={`navbar__toggle ${menuOpen ? 'navbar__toggle--open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      <div
        ref={menuRef}
        className={`mobile-menu ${menuOpen ? 'mobile-menu--open' : ''}`}
        onClick={() => setMenuOpen(false)}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <button
          className="mobile-menu__close"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          &times;
        </button>
        <ul className="mobile-menu__links" onClick={(e) => e.stopPropagation()}>
          {navItems.map(item => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`mobile-menu__link ${location.pathname === item.path ? 'mobile-menu__link--active' : ''}`}
                {...(location.pathname === item.path ? { 'aria-current': 'page' } : {})}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </Link>
            </li>
          ))}
          <li>
            <a
              href="https://lumecon.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-menu__link mobile-menu__link--external"
              onClick={() => setMenuOpen(false)}
            >
              Lumecon
            </a>
          </li>
        </ul>
      </div>
    </>
  );
}
